import { useMemo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider, parseSearchWith, stringifySearchWith } from '@tanstack/react-router'
import { APIProvider } from '@vis.gl/react-google-maps'
import { AuthProvider, useAuthContext, type AuthContextValue } from '@/common/contexts/AuthContext'
import { UserProfileProvider, useUserProfileContext, type UserProfileContextValue } from '@/common/contexts/UserProfileContext'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { AnalyticsProvider } from '@/common/analytics'
import { Heap } from '@/common/components/heap'
import { config } from './config'
import { routeTree } from './routeTree.gen'

/**
 * Router context type - provides auth and profile state to route guards.
 * This allows beforeLoad hooks to access auth state without React hooks.
 */
export interface RouterContext {
  auth: AuthContextValue
  profile: UserProfileContextValue
}

// Custom search param serialization - use plain strings instead of JSON
// This prevents values like "85201" from being serialized as "%2285201%22" (quoted)
const stringifySearch = stringifySearchWith((value) =>
  // Don't JSON stringify - just return the value as-is for strings
  typeof value === 'string' ? value : JSON.stringify(value)
)

const parseSearch = parseSearchWith((value) => {
  // Try to parse as JSON for backwards compatibility, but if it fails, return as string
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
})

// Create the router instance with context type
const router = createRouter({
  routeTree,
  trailingSlash: 'never',
  defaultPreload: 'intent',
  stringifySearch,
  parseSearch,
  context: {
    // These will be provided by RoutedApp via RouterProvider
    auth: undefined!,
    profile: undefined!,
  },
})

// Create a client outside the component to avoid recreating on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
})

// Register the router instance and context type for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
    routerContext: RouterContext
  }
}

/**
 * Router wrapper that provides auth and profile context to routes.
 * Must be inside AuthProvider and UserProfileProvider to access their contexts.
 * This enables beforeLoad guards to check auth state without React hooks.
 *
 * Also handles:
 * - Global auth loading state (loading spinner until auth determined)
 * - Cross-tab auth sync via localStorage
 * - All auth-related redirects
 */
function RoutedApp() {
  const auth = useAuthContext()
  const profile = useUserProfileContext()

  // Memoize the router context to prevent TanStack Router from re-evaluating
  // routes when the wrapper object reference changes but contents are the same
  const routerContext = useMemo(() => ({ auth, profile }), [auth, profile])

  // Show loading spinner while authentication or profile is being determined
  // This blocks the entire app until we have the full auth/profile state
  // For authenticated users, we also wait for profile to load so beforeLoad
  // guards can check needsOnboarding without needing useEffect fallbacks
  const isAuthLoading = auth.isLoading
  const isProfileLoading = auth.isAuthenticated && profile.isLoading

  // The SINGLE Heap loader + analytics lifecycle, mounted here (inside
  // Auth + UserProfile providers so the lifecycle can read user/profile, but
  // OUTSIDE RouterProvider — `usePageviewTracking` takes the router instance as
  // a param per the provider's design). Wrapping the loading branch too keeps
  // the provider/loader mounted across the auth-loading transition (no remount
  // churn). Everything is inert while `config.heapAppId` is unset, so this is
  // safe on pre-auth/marketing/login surfaces; the transport gates custom
  // track() on identify, so no event tracking fires before login.
  return (
    <AnalyticsProvider router={router}>
      <Heap />
      {isAuthLoading || isProfileLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}>
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <RouterProvider
          router={router}
          context={routerContext}
        />
      )}
    </AnalyticsProvider>
  )
}

/**
 * Main App component with providers and routing.
 *
 * Architecture:
 * - QueryClientProvider: Data fetching/caching with TanStack Query
 * - APIProvider: Google Maps API for Places autocomplete
 * - AuthProvider: Manages authentication via backend proxy to FusionAuth Login API
 * - UserProfileProvider: Shares user profile state across components
 * - AnalyticsProvider (in RoutedApp): single Heap loader + identify/pageview
 *   lifecycle; inert until `config.heapAppId` is set
 * - RouterProvider: File-based routing with TanStack Router (receives auth/profile context)
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <APIProvider apiKey={config.googleMapsApiKey ?? ''}>
        <AuthProvider>
          <UserProfileProvider>
            <RoutedApp />
          </UserProfileProvider>
        </AuthProvider>
      </APIProvider>
    </QueryClientProvider>
  )
}

export default App
